import { useEffect, useState, useMemo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { 
  Container, Grid, Card, CardContent, Typography, Box, LinearProgress, 
  Tooltip, Divider, Paper, Button, TextField, InputAdornment, MenuItem, 
  FormControl, InputLabel, Select, IconButton, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow, TablePagination, Dialog, DialogContent, 
  DialogTitle, Tabs, Tab, ToggleButton, ToggleButtonGroup, CircularProgress 
} from "@mui/material";
import { 
  Map, RoundaboutLeft, Assessment, Straighten, Business, ViewModule, Warehouse, 
  PieChart as PieChartIcon, Search as SearchIcon, Clear as ClearIcon, 
  Download as DownloadIcon, Close as CloseIcon, BarChart as BarChartIcon, 
  MeetingRoom as MeetingRoomIcon, Agriculture as AgricultureIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Home as HomeIcon,
  TrendingUp as TrendingUpIcon,
  LocationOn as LocationOnIcon
} from "@mui/icons-material";
import { IPDCWelcomeMessage } from "../../../../components";
import ViewMessageList from "../../Shared/Message/ViewMessageList";
import { infraImg, cadastreImg } from "../../../../assets/image/dashboard";
import { WideChartContainer } from "../../../../components/Charts";
import { GetDataFromApiWithParams } from "../../../../services/api/ExecuteApiRequests";
import { format } from "date-fns";
import {
  BPRequestVsResponseCard,
  OPRequestVsResponseCard,
  ShedParcelRatioCard,
  ImportedAndEditedCard,
  LandUseProportionCard,
} from "./DashboardDetail";

// Helper function to convert any value to number
const toNumber = (value) => {
  if (typeof value === 'number' && !isNaN(value)) return value;
  if (typeof value === 'string') {
    const num = parseFloat(value);
    return isNaN(num) ? 0 : num;
  }
  if (typeof value === 'object' && value !== null) {
    const num = parseFloat(value.toString());
    return isNaN(num) ? 0 : num;
  }
  return 0;
};

// Format number with commas
const formatNumber = (number) => {
  const num = toNumber(number);
  if (num === 0) return "0";
  return new Intl.NumberFormat('en-US').format(num);
};

// Format percentage
const formatPercentage = (value) => {
  const num = toNumber(value);
  if (num === 0) return "0%";
  return `${num.toFixed(1)}%`;
};

// Get color based on percentage value
const getProgressColor = (value) => {
  const num = toNumber(value);
  if (num >= 80) return "#1976d2";
  if (num >= 60) return "#1976d2";
  if (num >= 40) return "#795548";
  if (num >= 20) return "#795548";
  return "#795548";
};

// Progress Bar Component for cells
const ProgressBarCell = ({ value, label, actualValue, color }) => {
  const percentage = Math.min(100, Math.max(0, toNumber(value)));
  const barColor = color || getProgressColor(percentage);
  
  return (
    <Box sx={{ minWidth: 120 }}>
      <Tooltip title={`${label}: ${formatPercentage(percentage)}`} arrow>
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: barColor, fontWeight: 'bold' }}>
              {formatPercentage(percentage)}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={percentage}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: '#e0e0e0',
              '& .MuiLinearProgress-bar': {
                backgroundColor: barColor,
                borderRadius: 4,
              }
            }}
          />
        </Box>
      </Tooltip>
    </Box>
  );
};

// Land Use Pie Chart Component
const LandUsePieChart = ({ data, onClick }) => {
  const total = Object.values(data).reduce((sum, val) => sum + toNumber(val), 0);
  const colors = ['#1976d2', '#4caf50', '#795548'];
  
  if (total === 0) {
    return (
      <Card 
        sx={{ 
          bgcolor: '#E3F2FD',
          p: 2, 
          height: '100%', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          cursor: 'pointer',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 4
          }
        }} 
        onClick={onClick}
      >
        <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.85rem' }}>No land use data available</Typography>
      </Card>
    );
  }
  
  let startAngle = 0;
  const centerX = 65, centerY = 65, radius = 45;
  const svgSize = 130;
  
  const items = Object.keys(data).filter(key => toNumber(data[key]) > 0);
  
  return (
    <Card 
      sx={{ 
        bgcolor: '#E3F2FD',
        p: 1.5, 
        height: '100%', 
        cursor: 'pointer',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 4
        }
      }} 
      onClick={onClick}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1976d2', textAlign: 'center', mb: 0.5, fontSize: '0.8rem' }}>
        Land Use Proportion
      </Typography>
      
      <Typography variant="caption" color="textSecondary" sx={{ textAlign: 'center', display: 'block', mb: 1, fontSize: '0.7rem' }}>
        Total: {formatNumber(total)} m²
      </Typography>
      
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.2, pointerEvents: 'none' }}>
        <Box sx={{ position: 'relative', width: svgSize, height: svgSize }}>
          <svg width={svgSize} height={svgSize} viewBox={`0 0 ${svgSize} ${svgSize}`}>
            {items.map((key, index) => {
              const value = toNumber(data[key]);
              const percentage = (value / total) * 100;
              const angle = (percentage / 100) * 360;
              const endAngle = startAngle + angle;
              
              const startRad = (startAngle * Math.PI) / 180;
              const endRad = (endAngle * Math.PI) / 180;
              
              const x1 = centerX + radius * Math.cos(startRad);
              const y1 = centerY + radius * Math.sin(startRad);
              const x2 = centerX + radius * Math.cos(endRad);
              const y2 = centerY + radius * Math.sin(endRad);
              
              const largeArcFlag = angle > 180 ? 1 : 0;
              const pathData = `M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;
              
              const slice = (
                <path 
                  key={key} 
                  d={pathData} 
                  fill={colors[index % colors.length]} 
                  stroke="white" 
                  strokeWidth="1.5"
                  style={{ transition: 'opacity 0.3s' }}
                />
              );
              startAngle = endAngle;
              return slice;
            })}
          </svg>
        </Box>
        
        <Box sx={{ minWidth: 85 }}>
          {items.map((key, index) => {
            const value = toNumber(data[key]);
            const percentage = (value / total) * 100;
            return (
              <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.2, mb: 0.3 }}>
                <Box sx={{ width: 8, height: 8, bgcolor: colors[index % colors.length], borderRadius: 1 }} />
                <Typography variant="caption" sx={{ fontSize: '0.65rem', flex: 1 }}>
                  {key}
                </Typography>
                <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.6rem', color: colors[index % colors.length] }}>
                  {percentage.toFixed(0)}%
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Card>
  );
};

// Main Combined Dashboard Component
export default function LandInfraDevelopmentDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [parksData, setParksData] = useState([]);
  const [parcelsData, setParcelsData] = useState([]);
  const [buildingsData, setBuildingsData] = useState([]);
  const [shedsData, setShedsData] = useState([]);
  const [greenAreasData, setGreenAreasData] = useState([]);
  const [roadsData, setRoadsData] = useState([]);
  const [landLeasedData, setLandLeasedData] = useState([]);
  
  // State for building and occupancy stats (from code 2)
  const [buildingStats, setBuildingStats] = useState({
    totalRequests: 0,
    totalIssued: 0,
    totalPending: 0,
    totalOccupancyRequests: 0,
    totalResidentialBuildings: 0,
    totalCommercialBuildings: 0
  });
  const [occupancyStats, setOccupancyStats] = useState({
    totalRequests: 0,
    totalApproved: 0,
    totalPending: 0,
    approvalRate: 0,
    averageCompletionRate: 0
  });
  const [buildingPermits, setBuildingPermits] = useState([]);
  const [occupancyPermits, setOccupancyPermits] = useState([]);
  
  // State for calculated metrics
  const [summaryStats, setSummaryStats] = useState({
    occupancyRate: 0,
    parcelRatio: 0,
    totalParkArea: 0,
    totalOccupiedArea: 0,
    totalParcels: 0,
    totalOccupiedParcels: 0,
    totalParks: 0,
    totalSheds: 0,
    totalShedArea: 0,
    totalParcelArea: 0,
    totalBuildings: 0,
    buildingUtilization: 0,
    shedOccupancy: 0,
    totalActiveBuildings: 0,
    totalOccupiedSheds: 0
  });
  
  // State for leased land metrics
  const [leasedLandStats, setLeasedLandStats] = useState({
    totalLeasedLand: 0,
    occupiedLeasedLand: 0,
    landLeaseRatio: 0
  });
  
  // State for land use data
  const [landUseData, setLandUseData] = useState({
    'Parcels': 0,
    'Green Areas': 0,
    'Roads': 0
  });

  // State for yearly trends (from code 2)
  const [yearlyTrends, setYearlyTrends] = useState({});
  const [parkWiseStats, setParkWiseStats] = useState({});

  const blueColor = "#1976d2";
  const brownColor = "#795548";
  const greenColor = "#4caf50";
  const lightBlueBg = "#e3f2fd";
  const lightGrayBg = "#f5f5f5";

  const handleLandUseCardClick = () => {
    navigate("/landparkuse");
  };

  const handleLeasedLandCardClick = () => {
    navigate("/landleasedpark");
  };

  const getBuildingsForPark = useCallback((parkId) => {
    return buildingsData.filter(building => toNumber(building.park_id) === toNumber(parkId));
  }, [buildingsData]);

  const calculateGreenAreaTotal = useCallback((parkId) => {
    if (!greenAreasData || !Array.isArray(greenAreasData)) return 0;
    return greenAreasData
      .filter(item => toNumber(item.park_id) === toNumber(parkId))
      .reduce((sum, item) => {
        let area = toNumber(item.capacity || item.area || 0);
        return sum + area;
      }, 0);
  }, [greenAreasData]);

  const calculateRoadAreaTotal = useCallback((parkId) => {
    if (!roadsData || !Array.isArray(roadsData)) return 0;
    return roadsData
      .filter(item => toNumber(item.park_id) === toNumber(parkId))
      .reduce((sum, item) => {
        let area = toNumber(item.capacity || item.area || 0);
        if (area === 0 && item.width && item.geom) {
          area = toNumber(item.width) * 100;
        }
        return sum + area;
      }, 0);
  }, [roadsData]);

  const parkMetrics = useMemo(() => {
    const metrics = {};
    parksData.forEach(park => {
      const parkId = park.id;
      const parkArea = toNumber(park.area_on_plan || 0);
      const parkAreaM2 = parkArea;
      
      const parkParcels = parcelsData.filter(p => toNumber(p.park_id) === toNumber(parkId));
      
      let totalParcelArea = 0;
      parkParcels.forEach(parcel => {
        totalParcelArea += toNumber(parcel.area || 0);
      });
      
      const greenArea = calculateGreenAreaTotal(parkId);
      const roadArea = calculateRoadAreaTotal(parkId);
      const parkBuildings = getBuildingsForPark(parkId);
      const totalBuildingArea = parkBuildings.reduce((sum, b) => sum + toNumber(b.floor_area_m2), 0);
      const parkSheds = shedsData.filter(s => toNumber(s.park_id) === toNumber(parkId));
      const totalShedArea = parkSheds.reduce((sum, s) => sum + toNumber(s.shed_size), 0);
      
      const occupiedArea = parkParcels
        .filter(p => p.occupancy_status === "Occupied" || p.status === "Leased")
        .reduce((sum, p) => sum + toNumber(p.area), 0);
      
      const occupiedParcels = parkParcels.filter(p => p.occupancy_status === "Occupied" || p.status === "Leased").length;
      
      const occupancyRate = parkAreaM2 > 0 ? (occupiedArea / parkAreaM2) * 100 : 0;
      const parcelRatio = parkParcels.length > 0 ? (occupiedParcels / parkParcels.length) * 100 : 0;
      
      const activeBuildings = parkBuildings.filter(b => b.status === "Active").length;
      const buildingRatio = parkBuildings.length > 0 ? (activeBuildings / parkBuildings.length) * 100 : 0;
      
      const occupiedSheds = parkSheds.filter(s => s.status_utilization === "Occupied").length;
      const shedOccupancyRate = parkSheds.length > 0 ? (occupiedSheds / parkSheds.length) * 100 : 0;
      
      const greenAreaRatio = parkAreaM2 > 0 ? (greenArea / parkAreaM2) * 100 : 0;
      const roadRatio = parkAreaM2 > 0 ? (roadArea / parkAreaM2) * 100 : 0;
      
      metrics[park.name] = {
        park,
        totalArea: parkArea,
        totalAreaM2: parkAreaM2,
        totalParcelArea,
        occupiedArea,
        occupancyRate,
        totalParcels: parkParcels.length,
        occupiedParcels,
        parcelRatio,
        totalBuildings: parkBuildings.length,
        activeBuildings,
        buildingRatio,
        totalSheds: parkSheds.length,
        occupiedSheds,
        shedOccupancyRate,
        greenAreaRatio,
        roadRatio,
        totalBuildingArea,
        totalShedArea,
        greenArea,
        roadArea
      };
    });
    return metrics;
  }, [parksData, parcelsData, getBuildingsForPark, shedsData, calculateGreenAreaTotal, calculateRoadAreaTotal]);

  const calculateSummaryStats = useCallback(() => {
    let totalParkArea = 0;
    let totalOccupiedArea = 0;
    let totalParcels = 0;
    let totalOccupiedParcels = 0;
    let totalSheds = 0;
    let totalBuildings = 0;
    let totalActiveBuildings = 0;
    let totalOccupiedSheds = 0;
    let totalParcelArea = 0;
    let totalShedArea = 0;
    let totalGreenArea = 0;
    let totalRoadArea = 0;
    
    Object.values(parkMetrics).forEach(metric => {
      totalParkArea += metric.totalAreaM2;
      totalOccupiedArea += metric.occupiedArea;
      totalParcels += metric.totalParcels;
      totalOccupiedParcels += metric.occupiedParcels;
      totalSheds += metric.totalSheds;
      totalBuildings += metric.totalBuildings;
      totalActiveBuildings += metric.activeBuildings;
      totalOccupiedSheds += metric.occupiedSheds;
      totalParcelArea += metric.totalParcelArea || 0;
      totalShedArea += metric.totalShedArea || 0;
      totalGreenArea += metric.greenArea || 0;
      totalRoadArea += metric.roadArea || 0;
    });
    
    const overallOccupancyRate = totalParkArea > 0 ? (totalOccupiedArea / totalParkArea) * 100 : 0;
    const overallParcelRatio = totalParcels > 0 ? (totalOccupiedParcels / totalParcels) * 100 : 0;
    const overallBuildingUtilization = totalBuildings > 0 ? (totalActiveBuildings / totalBuildings) * 100 : 0;
    const overallShedOccupancy = totalSheds > 0 ? (totalOccupiedSheds / totalSheds) * 100 : 0;
    
    setSummaryStats({
      occupancyRate: overallOccupancyRate,
      parcelRatio: overallParcelRatio,
      totalParkArea,
      totalOccupiedArea,
      totalParcels,
      totalOccupiedParcels,
      totalParks: parksData.length,
      totalSheds: totalSheds,
      totalShedArea: totalShedArea,
      totalParcelArea: totalParcelArea,
      totalBuildings: totalBuildings,
      buildingUtilization: overallBuildingUtilization,
      shedOccupancy: overallShedOccupancy,
      totalActiveBuildings: totalActiveBuildings,
      totalOccupiedSheds: totalOccupiedSheds
    });
    
    setLandUseData({
      'Parcels': totalParcelArea,
      'Green Areas': totalGreenArea,
      'Roads': totalRoadArea
    });
  }, [parkMetrics, parksData.length]);

  // Calculate leased land statistics using landLeasedData from API
  const calculateLeasedLandStats = useCallback(() => {
    if (!landLeasedData || landLeasedData.length === 0) {
      setLeasedLandStats({
        totalLeasedLand: 0,
        occupiedLeasedLand: 0,
        landLeaseRatio: 0
      });
      return;
    }

    let totalLeasedLandArea = 0;
    let occupiedLeasedLandArea = 0;

    landLeasedData.forEach(item => {
      const parcel = parcelsData.find(p => p.upin === item.upin);
      const parcelArea = parcel ? toNumber(parcel.area || 0) : 0;
      
      totalLeasedLandArea += parcelArea;
      
      if (item.parcel_state === 'Occupied') {
        occupiedLeasedLandArea += parcelArea;
      }
    });
    
    const landLeaseRatio = totalLeasedLandArea > 0 ? (occupiedLeasedLandArea / totalLeasedLandArea) * 100 : 0;
    
    setLeasedLandStats({
      totalLeasedLand: totalLeasedLandArea,
      occupiedLeasedLand: occupiedLeasedLandArea,
      landLeaseRatio: landLeaseRatio
    });
  }, [landLeasedData, parcelsData]);

  // Calculate yearly trends and park wise stats (from code 2)
  const calculateYearlyTrendsAndParkStats = useCallback(() => {
    const yearlyData = {};
    const parkData = {};
    
    buildingPermits.forEach(permit => {
      // Yearly trends
      if (permit.requested_on) {
        const year = new Date(permit.requested_on).getFullYear().toString();
        if (!yearlyData[year]) {
          yearlyData[year] = { totalRequests: 0, totalIssued: 0, totalOccupancy: 0 };
        }
        yearlyData[year].totalRequests++;
        if (permit.status === "Finished" || permit.status === "Approved" || permit.status === "Issued") {
          yearlyData[year].totalIssued++;
        }
      }
      
      // Park wise stats
      const park = permit.park_name || "Unassigned";
      if (!parkData[park]) {
        parkData[park] = { totalRequests: 0, totalIssued: 0 };
      }
      parkData[park].totalRequests++;
      if (permit.status === "Finished" || permit.status === "Approved" || permit.status === "Issued") {
        parkData[park].totalIssued++;
      }
    });
    
    occupancyPermits.forEach(occ => {
      if (occ.requested_on) {
        const year = new Date(occ.requested_on).getFullYear().toString();
        if (yearlyData[year]) {
          yearlyData[year].totalOccupancy = (yearlyData[year].totalOccupancy || 0) + 1;
        } else if (occ.requested_on) {
          yearlyData[year] = { totalRequests: 0, totalIssued: 0, totalOccupancy: 1 };
        }
      }
    });
    
    setYearlyTrends(yearlyData);
    setParkWiseStats(parkData);
  }, [buildingPermits, occupancyPermits]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [parksummary, parcelsummary, buildingsummary, shedsummary, greenAreaData, roadData, leasedland, buildingRes, occupancyRes] = await Promise.all([
          GetDataFromApiWithParams(`parks/parksummary`),
          GetDataFromApiWithParams(`parks/parcelsummary`),
          GetDataFromApiWithParams(`parks/buildingsummary`),
          GetDataFromApiWithParams(`parks/shedsummary`),
          GetDataFromApiWithParams(`parks/greenareasummary`),
          GetDataFromApiWithParams(`parks/roadsummary`),
          GetDataFromApiWithParams(`parks/landleasedsummary`),
          GetDataFromApiWithParams(`design-management/building-permits1`),
          GetDataFromApiWithParams(`design-management/occupancy-permits`)
        ]);
        
        setParksData(parksummary || []);
        setParcelsData(parcelsummary || []);
        setGreenAreasData(greenAreaData || []);
        setRoadsData(roadData || []);
        setLandLeasedData(leasedland || []);
        
        const processedBuildings = (buildingsummary || []).map(building => {
          let floorArea = toNumber(building.floor_area || building.area || building.building_area || 0);
          if (floorArea === 0 && building.no_of_floors) {
            floorArea = toNumber(building.no_of_floors) * 500;
          }
          return {
            id: building.id,
            park_id: parseInt(building.park_id),
            status: building.status || "Active",
            name: building.name || `Building ${building.id}`,
            bldg_no: building.bldg_no || building.block_id || `B-${building.id}`,
            no_of_floors: toNumber(building.no_of_floors || 1),
            no_of_units: toNumber(building.no_of_units || 0),
            floor_area_m2: floorArea,
            occupancy_ratio: toNumber(building.occupancy_ratio || building.occupancy_rate || 0)
          };
        });
        
        const processedSheds = (shedsummary || []).map(shed => ({
          id: shed.id,
          park_id: toNumber(shed.park_id),
          upin: shed.upin || null,
          status_utilization: shed.status_utilization || "Vacant",
          shed_size: toNumber(shed.shed_size || shed.area || 0)
        }));
        
        setBuildingsData(processedBuildings);
        setShedsData(processedSheds);
        
        // Process building permit data (from code 2)
        let buildingData = [];
        let occupancyData = [];
        
        if (buildingRes && Array.isArray(buildingRes)) buildingData = buildingRes;
        else if (buildingRes?.data && Array.isArray(buildingRes.data)) buildingData = buildingRes.data;
        
        if (occupancyRes && Array.isArray(occupancyRes)) occupancyData = occupancyRes;
        else if (occupancyRes?.data && Array.isArray(occupancyRes.data)) occupancyData = occupancyRes.data;
        
        const formattedBuildingPermits = buildingData.map(permit => ({
          id: permit.id,
          permit_no: permit.permit_no || `BP-${permit.id}`,
          status: permit.status || "Pending",
          no_of_residence_bldgs: permit.no_of_residence_bldgs || 0,
          no_of_commercial_bldgs: permit.no_of_commercial_bldgs || 0,
          requested_on: permit.requested_on,
          park_name: permit.park_name || "N/A"
        }));
        
        const formattedOccupancyPermits = occupancyData.map(occ => ({
          id: occ.id,
          building_permit_id: occ.building_permit_id,
          status: occ.status || "Pending",
          requested_on: occ.requested_on
        }));
        
        setBuildingPermits(formattedBuildingPermits);
        setOccupancyPermits(formattedOccupancyPermits);
        
        // Calculate Building Permit Statistics
        const totalBuildingRequests = formattedBuildingPermits.length;
        const totalIssued = formattedBuildingPermits.filter(p => p.status === "Finished" || p.status === "Approved" || p.status === "Issued").length;
        const totalBuildingPending = formattedBuildingPermits.filter(p => p.status === "REQUESTED" || p.status === "Pending" || p.status === "Under Review").length;
        const totalResidential = formattedBuildingPermits.reduce((sum, p) => sum + (p.no_of_residence_bldgs || 0), 0);
        const totalCommercial = formattedBuildingPermits.reduce((sum, p) => sum + (p.no_of_commercial_bldgs || 0), 0);
        const totalOccupancy = formattedOccupancyPermits.length;
        
        setBuildingStats({
          totalRequests: totalBuildingRequests,
          totalIssued: totalIssued,
          totalPending: totalBuildingPending,
          totalOccupancyRequests: totalOccupancy,
          totalResidentialBuildings: totalResidential,
          totalCommercialBuildings: totalCommercial
        });
        
        // Calculate Occupancy Permit Statistics
        const totalOccupancyRequests = formattedOccupancyPermits.length;
        const totalApproved = formattedOccupancyPermits.filter(p => p.status === "Finished" || p.status === "Approved" || p.status === "approved").length;
        const totalOccupancyPending = formattedOccupancyPermits.filter(p => p.status === "REQUESTED" || p.status === "Pending" || p.status === "UNDER_REVIEW").length;
        const approvalRate = totalOccupancyRequests > 0 ? (totalApproved / totalOccupancyRequests) * 100 : 0;
        
        setOccupancyStats({
          totalRequests: totalOccupancyRequests,
          totalApproved: totalApproved,
          totalPending: totalOccupancyPending,
          approvalRate: approvalRate,
          averageCompletionRate: 0
        });
        
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  useEffect(() => {
    if (Object.keys(parkMetrics).length > 0) {
      calculateSummaryStats();
    }
  }, [parkMetrics, calculateSummaryStats]);

  useEffect(() => {
    if (landLeasedData.length > 0 && parcelsData.length > 0) {
      calculateLeasedLandStats();
    }
  }, [landLeasedData, parcelsData, calculateLeasedLandStats]);

  useEffect(() => {
    if (buildingPermits.length > 0 || occupancyPermits.length > 0) {
      calculateYearlyTrendsAndParkStats();
    }
  }, [buildingPermits, occupancyPermits, calculateYearlyTrendsAndParkStats]);

  return (
    <Box sx={{ bgcolor: '#f4f9ff', width: '100%', mx: 0, py: 1 }}>
      {/* Header */}
      <Box sx={{ color: 'white', py: 2, px: 3 }}>
        <Container maxWidth="xl">
          <Box>
            <Grid item xs={12} md={12}>
              <IPDCWelcomeMessage messageTitle="L & I Development, Deputy CEO" />
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* Summary Statistics Row */}
      <Container maxWidth="xl" sx={{ mt: 2 }}>
        <Grid container spacing={2}>
          {/* Total Parks Card */}
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ p: 2, bgcolor: lightBlueBg, boxShadow: 2, height: '100%', width: '100%' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                <Box>
                  <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>Total Parks</Typography>
                  <Typography variant="h5" fontWeight="bold" color={blueColor} sx={{ fontSize: '1.8rem' }}>
                    {/* {formatNumber(summaryStats.totalParks)} */}
                    13
                  </Typography>
                </Box>
                <Business sx={{ fontSize: 32, color: blueColor }} />
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box>
                <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>Total Area</Typography>
                <Typography variant="h6" fontWeight="bold" color={brownColor} sx={{ fontSize: '1.1rem' }}>
                  {formatNumber(summaryStats.totalParkArea)} m²
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                  {(summaryStats.totalParkArea / 10000).toFixed(2)} ha
                </Typography>
                <Box mt={1.5}>
                  <LinearProgress 
                    variant="determinate" 
                    value={summaryStats.occupancyRate} 
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: '#e0e0e0',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: brownColor,
                        borderRadius: 3,
                      }
                    }}
                  />
                  <Typography variant="caption" display="block" mt={0.5} sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                    {formatPercentage(summaryStats.occupancyRate)} Occupied
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>
          
          {/* Total Parcels Card */}
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ p: 2, bgcolor: lightBlueBg, boxShadow: 2, height: '100%', width: '100%' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                <Box>
                  <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>Total Parcels</Typography>
                  <Typography variant="h5" fontWeight="bold" color={blueColor} sx={{ fontSize: '1.8rem' }}>
                    {formatNumber(summaryStats.totalParcels)}
                  </Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.75rem', display: 'block', mt: 0.5 }}>
                    {formatNumber(summaryStats.totalOccupiedParcels)} Occupied
                  </Typography>
                </Box>
                <Box sx={{ bgcolor: `${blueColor}15`, borderRadius: '50%', p: 1 }}>
                  <ViewModule sx={{ fontSize: 32, color: blueColor }} />
                </Box>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box>
                <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                  Parcel Area
                </Typography>
                <Typography variant="h6" fontWeight="bold" color={brownColor} sx={{ fontSize: '0.9rem' }}>
                  {formatNumber(summaryStats.totalParcelArea)} m² ({ (summaryStats.totalParcelArea / 10000).toFixed(2)} ha)
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, mt: 1 }}>
                  <Typography variant="caption" sx={{ fontSize: '0.85rem', color: '#666', fontWeight: 500 }}>Parcel Occupancy</Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.75rem', color: blueColor, fontWeight: 'bold' }}>
                    {formatPercentage(summaryStats.parcelRatio)}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, Math.max(0, toNumber(summaryStats.parcelRatio)))}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: '#e0e0e0',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: brownColor,
                      borderRadius: 3,
                    }
                  }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#666' }}>
                    {formatNumber(summaryStats.totalOccupiedParcels)} / {formatNumber(summaryStats.totalParcels)}
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>

          {/* Total Sheds Card */}
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ p: 2, bgcolor: lightBlueBg, boxShadow: 2, height: '100%', width: '100%' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                <Box>
                  <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>Total Sheds</Typography>
                  <Typography variant="h5" fontWeight="bold" color={blueColor} sx={{ fontSize: '1.8rem' }}>
                    {formatNumber(summaryStats.totalSheds)}
                  </Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.75rem', display: 'block', mt: 0.5 }}>
                    {formatNumber(summaryStats.totalOccupiedSheds)} Occupied
                  </Typography>
                </Box>
                <Box sx={{ bgcolor: `${brownColor}15`, borderRadius: '50%', p: 1 }}>
                  <Warehouse sx={{ fontSize: 32, color: brownColor }} />
                </Box>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box>
                <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                  Shed Area
                </Typography>
                <Typography variant="h6" fontWeight="bold" color={brownColor} sx={{ fontSize: '0.9rem' }}>
                  {formatNumber(summaryStats.totalShedArea)} m² ({ (summaryStats.totalShedArea / 10000).toFixed(2)} ha)
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, mt: 1 }}>
                  <Typography variant="caption" sx={{ fontSize: '0.85rem', color: '#666', fontWeight: 500 }}>Shed Occupancy</Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.75rem', color: brownColor, fontWeight: 'bold' }}>
                    {formatPercentage(summaryStats.shedOccupancy)}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, Math.max(0, toNumber(summaryStats.shedOccupancy)))}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: '#e0e0e0',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: brownColor,
                      borderRadius: 3,
                    }
                  }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#666' }}>
                    {formatNumber(summaryStats.totalOccupiedSheds)} / {formatNumber(summaryStats.totalSheds)}
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>

          {/* Total Leased Land Card */}
          <Grid item xs={12} sm={6} md={2.4}>
            <Card 
              sx={{ 
                p: 2, 
                bgcolor: lightBlueBg, 
                boxShadow: 2, 
                height: '100%', 
                width: '100%',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4
                }
              }}
              onClick={handleLeasedLandCardClick}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                <Box>
                  <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>Total Service Land</Typography>
                  <Typography variant="h5" fontWeight="bold" color={blueColor} sx={{ fontSize: '1.4rem' }}>
                    {formatNumber(leasedLandStats.totalLeasedLand)} m²
                  </Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.75rem', display: 'block', mt: 0.5 }}>
                    {(leasedLandStats.totalLeasedLand / 10000).toFixed(2)} ha
                  </Typography>
                </Box>
                <Box sx={{ bgcolor: `${greenColor}15`, borderRadius: '50%', p: 1 }}>
                  <PieChartIcon sx={{ fontSize: 32, color: greenColor }} />
                </Box>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontSize: '0.85rem', color: '#666', fontWeight: 500 }}>Service Land Ratio</Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.75rem', color: brownColor, fontWeight: 'bold' }}>
                    {formatPercentage(leasedLandStats.landLeaseRatio)}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, Math.max(0, toNumber(leasedLandStats.landLeaseRatio)))}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: '#e0e0e0',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: brownColor,
                      borderRadius: 3,
                    }
                  }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#666' }}>
                    {formatNumber(leasedLandStats.occupiedLeasedLand)} / {formatNumber(leasedLandStats.totalLeasedLand)}
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>

          {/* Land Use Proportion Card */}
          <Grid item xs={12} sm={6} md={2.4}>
            <LandUsePieChart data={landUseData} onClick={handleLandUseCardClick} />
          </Grid>
        </Grid>
      </Container>

      {/* Occupancy Ratio and Parcel Ratio Row */}
      <Container maxWidth="xl" sx={{ mt: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Card sx={{ height: '100%', bgcolor: lightGrayBg, boxShadow: 2, borderRadius: 2 }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: brownColor, fontSize: '0.85rem' }}>
                    Occupancy Ratio
                  </Typography>
                  <Box sx={{ bgcolor: `${brownColor}15`, borderRadius: '50%', p: 0.75 }}>
                    <Assessment sx={{ fontSize: 24, color: brownColor }} />
                  </Box>
                </Box>
                
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: brownColor, mb: 1, fontSize: '1.8rem' }}>
                  {formatPercentage(summaryStats.occupancyRate)}
                </Typography>
                
                <Typography variant="caption" color="textSecondary" gutterBottom sx={{ fontSize: '0.65rem', display: 'block' }}>
                  {formatNumber(summaryStats.totalOccupiedArea)} m² Occupied / {formatNumber(summaryStats.totalParkArea)} m² Total
                </Typography>
                
                <Box sx={{ mt: 1.5 }}>
                  <ProgressBarCell 
                    value={summaryStats.occupancyRate} 
                    label="Overall Occupancy" 
                    actualValue={summaryStats.occupancyRate}
                    color={brownColor}
                  />
                </Box>
                
                <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #e0e0e0' }}>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.6rem' }}>Available Area</Typography>
                      <Typography variant="body2" fontWeight="bold" color={brownColor} sx={{ fontSize: '0.75rem' }}>
                        {formatNumber(summaryStats.totalParkArea - summaryStats.totalOccupiedArea)} m²
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.6rem' }}>Vacancy Rate</Typography>
                      <Typography variant="body2" fontWeight="bold" color={brownColor} sx={{ fontSize: '0.75rem' }}>
                        {formatPercentage(100 - summaryStats.occupancyRate)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <Card sx={{ height: '100%', bgcolor: lightGrayBg, boxShadow: 2, borderRadius: 2 }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: blueColor, fontSize: '0.85rem' }}>
                    Parcel Ratio
                  </Typography>
                  <Box sx={{ bgcolor: `${blueColor}15`, borderRadius: '50%', p: 0.75 }}>
                    <Straighten sx={{ fontSize: 24, color: blueColor }} />
                  </Box>
                </Box>
                
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: blueColor, mb: 1, fontSize: '1.8rem' }}>
                  {formatPercentage(summaryStats.parcelRatio)}
                </Typography>
                
                <Typography variant="caption" color="textSecondary" gutterBottom sx={{ fontSize: '0.65rem', display: 'block' }}>
                  {formatNumber(summaryStats.totalOccupiedParcels)} Parcels Occupied / {formatNumber(summaryStats.totalParcels)} Total
                </Typography>
                
                <Box sx={{ mt: 1.5 }}>
                  <ProgressBarCell 
                    value={summaryStats.parcelRatio} 
                    label="Parcel Occupancy" 
                    actualValue={summaryStats.parcelRatio}
                    color={blueColor}
                  />
                </Box>
                
                <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #e0e0e0' }}>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.6rem' }}>Vacant Parcels</Typography>
                      <Typography variant="body2" fontWeight="bold" color={blueColor} sx={{ fontSize: '0.75rem' }}>
                        {formatNumber(summaryStats.totalParcels - summaryStats.totalOccupiedParcels)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.6rem' }}>Available Ratio</Typography>
                      <Typography variant="body2" fontWeight="bold" color={blueColor} sx={{ fontSize: '0.75rem' }}>
                        {formatPercentage(100 - summaryStats.parcelRatio)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* ROW 1 FROM CODE 2: Building and Occupancy Permit Overview Boxes */}
      <Container maxWidth="xl" sx={{ mt: 2 }}>
        <Grid container spacing={2}>
          {/* Building Permit Overview Box */}
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 2, borderRadius: 2, bgcolor: '#e3f2fd', height: '100%' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1565c0', mb: 2 }}>
                Building Permit Overview
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6} md={4}>
                  <Card sx={{ bgcolor: 'white', boxShadow: 1, height: '100%' }}>
                    <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                      <AssignmentIcon sx={{ color: '#1976d2', fontSize: 24 }} />
                      <Typography variant="h5" sx={{ color: '#1a237e', fontWeight: 'bold' }}>
                        {formatNumber(buildingStats.totalRequests)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666' }}>Total Building Permits</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={4}>
                  <Card sx={{ bgcolor: 'white', boxShadow: 1, height: '100%' }}>
                    <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                      <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 24 }} />
                      <Typography variant="h5" sx={{ color: '#1a237e', fontWeight: 'bold' }}>
                        {formatNumber(buildingStats.totalIssued)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666' }}>Permits Issued</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={4}>
                  <Card sx={{ bgcolor: 'white', boxShadow: 1, height: '100%' }}>
                    <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                      <PendingIcon sx={{ color: '#ed6c02', fontSize: 24 }} />
                      <Typography variant="h5" sx={{ color: '#1a237e', fontWeight: 'bold' }}>
                        {formatNumber(buildingStats.totalPending)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666' }}>Pending Requests</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Occupancy Permit Overview Box */}
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 2, borderRadius: 2, bgcolor: '#e3f2fd', height: '100%' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#6a1b9a', mb: 2 }}>
                Occupancy Permit Overview
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6} md={4}>
                  <Card sx={{ bgcolor: 'white', boxShadow: 1, height: '100%' }}>
                    <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                      <AssignmentIcon sx={{ color: '#1976d2', fontSize: 24 }} />
                      <Typography variant="h5" sx={{ color: '#1a237e', fontWeight: 'bold' }}>
                        {formatNumber(occupancyStats.totalRequests)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666' }}>Total Occupancy Requests</Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Card sx={{ bgcolor: 'white', boxShadow: 1, height: '100%' }}>
                    <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                      <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 24 }} />
                      <Typography variant="h5" sx={{ color: '#1a237e', fontWeight: 'bold' }}>
                        {formatNumber(occupancyStats.totalApproved)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666' }}>Approved</Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Card sx={{ bgcolor: 'white', boxShadow: 1, height: '100%' }}>
                    <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                      <PendingIcon sx={{ color: '#ed6c02', fontSize: 24 }} />
                      <Typography variant="h5" sx={{ color: '#1a237e', fontWeight: 'bold' }}>
                        {formatNumber(occupancyStats.totalPending)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666' }}>Pending</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* L&I Development Dashboard Cards */}
      <Container maxWidth="xl" sx={{ mt: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <BPRequestVsResponseCard />
          </Grid>
          <Grid item xs={12} md={6}>
            <OPRequestVsResponseCard />
          </Grid>
        </Grid>
      </Container>

      {/* ROW 3 FROM CODE 2: Park-wise Building Permit Requests + Yearly Trend Summary */}
      <Container maxWidth="xl" sx={{ mt: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 2, borderRadius: 2, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LocationOnIcon sx={{ color: '#1976d2', mr: 1 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
                  Building Permits by Park
                </Typography>
              </Box>
              <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                {Object.keys(parkWiseStats).length > 0 ? (
                  Object.entries(parkWiseStats).map(([park, stats]) => {
                    const percentage = buildingStats.totalRequests > 0 
                      ? ((stats.totalRequests / buildingStats.totalRequests) * 100).toFixed(1) 
                      : 0;
                    return (
                      <Box key={park} sx={{ mb: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>{park}</Typography>
                          <Typography variant="body2" color="textSecondary">{stats.totalRequests} requests ({percentage}%)</Typography>
                        </Box>
                        <Box sx={{ width: '100%', bgcolor: '#e0e0e0', borderRadius: 1, height: 8, overflow: 'hidden' }}>
                          <Box 
                            sx={{ 
                              width: `${percentage}%`, 
                              bgcolor: '#1976d2', 
                              height: '100%',
                              borderRadius: 1
                            }} 
                          />
                        </Box>
                      </Box>
                    );
                  })
                ) : (
                  <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 3 }}>
                    No park data available
                  </Typography>
                )}
              </Box>
              <Box sx={{ mt: 2, pt: 1, borderTop: '1px solid #e0e0e0' }}>
                <Typography variant="caption" color="textSecondary">
                  Total Parks with requests: {Object.keys(parkWiseStats).length}
                </Typography>
              </Box>
            </Paper>
          </Grid>

          {/* Yearly Trend Summary */}
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 2, borderRadius: 2, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingUpIcon sx={{ color: '#1976d2', mr: 1 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
                  Yearly Trend Summary
                </Typography>
              </Box>
              <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                {Object.keys(yearlyTrends).length > 0 ? (
                  Object.entries(yearlyTrends)
                    .sort((a, b) => a[0] - b[0])
                    .map(([year, data]) => (
                      <Box key={year} sx={{ mb: 2, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1a237e' }}>{year}</Typography>
                        <Grid container spacing={1} sx={{ mt: 0.5 }}>
                          <Grid item xs={4}>
                            <Typography variant="caption" color="textSecondary">Total Requests</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                              {formatNumber(data.totalRequests)}
                            </Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography variant="caption" color="textSecondary">Permits Issued</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                              {formatNumber(data.totalIssued || 0)}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    ))
                ) : (
                  <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 3 }}>
                    No yearly data available
                  </Typography>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Map Cards */}
      <Container maxWidth="xl" sx={{ mt: 2 }}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} md={5}>
            <Link to="/viewCadastreMap" style={{ textDecoration: 'none' }}>
              <WideChartContainer
                THECHART={<img src={cadastreImg} alt="Cadastre" height={150} width="100%" />}
                title="Cadastre"
                link="/viewCadastreMap"
                showMoreButton={false}
                avatar={<Map />}
              />
            </Link>
          </Grid>
          <Grid item xs={12} md={5}>
            <Link to="/infrastructureMap" style={{ textDecoration: 'none' }}>
              <WideChartContainer
                THECHART={<img src={infraImg} alt={"Infrastructure"} height={150} width="100%" />}
                title="Infrastructure"
                link="/infrastructureMap"
                showMoreButton={false}
                avatar={<RoundaboutLeft />}
              />
            </Link>
          </Grid>
        </Grid>
      </Container>

     
    </Box>
  );
}